/**
 * Shared state and navigation for the seed → email + OTP account migration.
 *
 * Seed accounts are detected from their derived anonymous email or their
 * pre-OTP wrapped key. The migration screen is opened automatically once per
 * session and can be opened manually from Settings or onboarding.
 */
export function useSeedMigration() {
  const needsMigration = useState('seed-migration-needed', () => false)
  const redirected = useState('seed-migration-redirected', () => false)
  const route = useRoute()
  const user = useSupabaseUser()
  const auth = useAuth()

  async function refresh(): Promise<boolean> {
    try {
      needsMigration.value = user.value ? await auth.isSeedAccount() : false
    } catch (error) {
      console.error('[seed-migration] Could not check account type:', error)
      needsMigration.value = false
    }
    return needsMigration.value
  }

  /**
   * Redirects to /migrate once per session when the signed-in account is
   * still seed-based. Navigating away ("Continue later") is respected for
   * the rest of the session.
   */
  async function maybeRedirect(): Promise<void> {
    if (redirected.value || !user.value) return
    if (route.path === '/migrate') return

    if (!(await refresh())) return

    redirected.value = true
    await navigateTo('/migrate')
  }

  function open(): void {
    navigateTo('/migrate')
  }

  /** Stops the automatic redirect for the rest of the session. */
  function dismiss(): void {
    redirected.value = true
  }

  return {
    needsMigration,
    refresh,
    maybeRedirect,
    open,
    dismiss
  }
}
